import api from './api';

export interface KeisukeDiscussionAuthor {
  id: number;
  username: string;
}

export interface KeisukeDiscussionTag {
  id: number;
  tag: string;
}

export interface KeisukeDiscussionThreadListItem {
  id: number;
  title: string;
  excerpt: string;
  status: string;
  priority: string;
  viewCount: number;
  commentCount: number;
  analysisId: number | null;
  createdAt: string;
  updatedAt: string;
  user: KeisukeDiscussionAuthor;
  tags: KeisukeDiscussionTag[];
}

export interface KeisukeDiscussionComment {
  id: number;
  threadId: number;
  userId: number;
  parentId: number | null;
  content: string;
  createdAt: string;
  updatedAt: string;
  user: KeisukeDiscussionAuthor;
}

export interface KeisukeDiscussionThreadDetail {
  id: number;
  userId: number;
  analysisId: number | null;
  title: string;
  content: string;
  status: string;
  priority: string;
  viewCount: number;
  commentCount: number;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  resolvedBy: number | null;
  user: KeisukeDiscussionAuthor;
  tags: KeisukeDiscussionTag[];
  comments: KeisukeDiscussionComment[];
}

export interface KeisukeDiscussionListResponse {
  success: boolean;
  data: KeisukeDiscussionThreadListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const keisukeDiscussionService = {
  async list(
    params?: {
      page?: number;
      limit?: number;
      status?: string;
      tag?: string;
      sort?: 'recent' | 'popular';
      q?: string;
    },
    signal?: AbortSignal
  ): Promise<KeisukeDiscussionListResponse> {
    const response = await api.get<KeisukeDiscussionListResponse>('/keisuke/discussion/threads', {
      params,
      signal,
    });
    return response.data;
  },

  async getById(id: number, signal?: AbortSignal): Promise<{ success: boolean; data: KeisukeDiscussionThreadDetail }> {
    const response = await api.get<{ success: boolean; data: KeisukeDiscussionThreadDetail }>(
      `/keisuke/discussion/threads/${id}`,
      { signal }
    );
    return response.data;
  },

  async createThread(
    body: {
      title: string;
      content: string;
      tags?: string[];
      analysisId?: number | null;
      priority?: 'high' | 'medium' | 'low';
    },
    signal?: AbortSignal
  ): Promise<{ success: boolean; data: KeisukeDiscussionThreadDetail }> {
    const response = await api.post<{ success: boolean; data: KeisukeDiscussionThreadDetail }>(
      '/keisuke/discussion/threads',
      body,
      signal ? { signal } : undefined
    );
    return response.data;
  },

  async addComment(
    threadId: number,
    body: { content: string; parentId?: number | null },
    signal?: AbortSignal
  ): Promise<{ success: boolean; data: KeisukeDiscussionComment }> {
    const response = await api.post<{ success: boolean; data: KeisukeDiscussionComment }>(
      `/keisuke/discussion/threads/${threadId}/comments`,
      body,
      signal ? { signal } : undefined
    );
    return response.data;
  },

  async editComment(
    threadId: number,
    commentId: number,
    body: { content: string },
    signal?: AbortSignal
  ): Promise<{ success: boolean; data: KeisukeDiscussionComment }> {
    const response = await api.patch<{ success: boolean; data: KeisukeDiscussionComment }>(
      `/keisuke/discussion/threads/${threadId}/comments/${commentId}`,
      body,
      signal ? { signal } : undefined
    );
    return response.data;
  },

  async deleteComment(
    threadId: number,
    commentId: number,
    signal?: AbortSignal
  ): Promise<{ success: boolean; data: { success: boolean } }> {
    const response = await api.delete<{ success: boolean; data: { success: boolean } }>(
      `/keisuke/discussion/threads/${threadId}/comments/${commentId}`,
      signal ? { signal } : undefined
    );
    return response.data;
  },

  async resolve(threadId: number, signal?: AbortSignal): Promise<{ success: boolean; data: unknown }> {
    const response = await api.post<{ success: boolean; data: unknown }>(
      `/keisuke/discussion/threads/${threadId}/resolve`,
      {},
      signal ? { signal } : undefined
    );
    return response.data;
  },

  async reopen(threadId: number, signal?: AbortSignal): Promise<{ success: boolean; data: unknown }> {
    const response = await api.post<{ success: boolean; data: unknown }>(
      `/keisuke/discussion/threads/${threadId}/reopen`,
      {},
      signal ? { signal } : undefined
    );
    return response.data;
  },

  async lock(threadId: number, signal?: AbortSignal): Promise<{ success: boolean; data: unknown }> {
    const response = await api.post<{ success: boolean; data: unknown }>(
      `/keisuke/discussion/threads/${threadId}/lock`,
      {},
      signal ? { signal } : undefined
    );
    return response.data;
  },

  async unlock(threadId: number, signal?: AbortSignal): Promise<{ success: boolean; data: unknown }> {
    const response = await api.post<{ success: boolean; data: unknown }>(
      `/keisuke/discussion/threads/${threadId}/unlock`,
      {},
      signal ? { signal } : undefined
    );
    return response.data;
  },
};
